import WebApp from '@twa-dev/sdk'

import { authApiClient, authTokenLocalStorageKey } from '@/lib/api/lib/client'
import { decodeJwtPayload } from '@/lib/jwt'

export { decodeJwtPayload } from '@/lib/jwt'

function getCookieByName(name: string): string | null {
    const cookies = document.cookie.split('; ')
    const cookie = cookies.find((c) => c.startsWith(`${name}=`))
    if (cookie) {
        return cookie.split('=')[1]
    }
    return null
}

// Entitlement helpers read the payload on every render; decode each token once.
let decodedToken: string | null = null
let decodedPayload: Record<string, unknown> | null = null

export function getTokenPayload(): Record<string, unknown> | null {
    const token =
        localStorage.getItem(authTokenLocalStorageKey) ||
        getCookieByName('appSession')
    if (!token) return null
    if (token === decodedToken) return decodedPayload

    try {
        decodedPayload = decodeJwtPayload(token)
    } catch (error) {
        console.error('Failed to parse auth token payload:', error)
        decodedPayload = null
    }
    decodedToken = token
    return decodedPayload
}

export function getUserId(): string {
    const payload = getTokenPayload()
    if (!payload) return ''
    return payload['jti'] as string
}

export function getTelegramUserId(): string {
    const payload = getTokenPayload()
    if (!payload) return ''
    return payload['sub'] as string
}

/** Normalize JWT `aud` which may be a string or string[]. */
export function normalizeAud(aud: unknown): string[] {
    if (Array.isArray(aud)) {
        return aud.filter((item): item is string => typeof item === 'string')
    }
    if (typeof aud === 'string' && aud.length > 0) {
        return [aud]
    }
    return []
}

export function getUserAttributes(): string[] {
    const payload = getTokenPayload()
    if (!payload) return []
    return normalizeAud(payload['aud'])
}

/**
 * Auth token changes are invisible to React: every reader below goes straight
 * to localStorage. Components that branch on an entitlement -- isCoach() and
 * friends -- subscribe through useAuthSession so a refreshed token re-renders
 * them instead of waiting for a reload.
 */
type AuthSessionListener = () => void

const authSessionListeners = new Set<AuthSessionListener>()
let authSessionVersion = 0

export function subscribeAuthSession(
    listener: AuthSessionListener
): () => void {
    authSessionListeners.add(listener)
    return () => {
        authSessionListeners.delete(listener)
    }
}

export function getAuthSessionSnapshot(): number {
    return authSessionVersion
}

export function notifyAuthSessionChanged(): void {
    authSessionVersion += 1
    for (const listener of authSessionListeners) {
        listener()
    }
}

/**
 * Replace the stored session token with one the gateway just issued (e.g. the
 * token a Stars refund returns without Pro) and re-render entitlement readers.
 */
export function storeSessionToken(token: string): void {
    localStorage.setItem(authTokenLocalStorageKey, token)
    notifyAuthSessionChanged()
}

/**
 * Mint a token carrying the current entitlements. `/auth/exchange` rebuilds
 * the `aud` claim from the ABAC store on every call, so this is how a grant
 * made outside the session -- a coach approval, say -- reaches the client.
 *
 * Returns false rather than throwing, and never clears the existing token: a
 * failure here usually means the Telegram initData aged out past
 * TG_BOT_TOKEN_EXPIRATION, and the old token is still perfectly valid. See
 * the same reasoning in src/hooks/token.tsx.
 */
export async function refreshSession(): Promise<boolean> {
    try {
        const fresh = await authApiClient.exchange({
            v1AuthExchangeRequest: { initData: WebApp.initData }
        })
        if (!fresh.token) return false

        storeSessionToken(fresh.token)
        return true
    } catch (error) {
        console.error('Failed to refresh auth session:', error)
        return false
    }
}
